import { useState } from 'react';

import type { Booking, BookingGarment } from '@/api/bookings';
import { useGarmentInspection } from '@/api/inspections';
import { Button, Card, Notice } from '@/components/PageHeading';
import type { toRequest } from '@core/returns/inspection';

import { InspectionPage } from '../returns/InspectionPage';
import { CounterBand } from './CounterBand';

/**
 * Check each garment as it comes back, one after another — the phone's `app/booking/inspect.tsx`
 * on the website (2026-10-22). Taking garments back leads here, and a booking with garments back and
 * unchecked offers it, so a return checked later has a way in.
 *
 * <p>
 * The garments are the ones back and not yet looked at, <strong>fixed when the screen opens</strong>
 * — each one saved moves on to the next, so the list must not shrink under the clerk. Each is the
 * returns module's own `InspectionPage` and its shared rules (`@core/returns/inspection`); the save
 * is the page's, as every write on a booking is.
 * </p>
 */
export function CheckGarments({
  booking,
  saving,
  onSave,
  onDone,
}: {
  booking: Booking;
  saving: boolean;
  /** Saves one garment's inspection; resolves once it is recorded. */
  onSave: (rentalOrderItemId: number, payload: ReturnType<typeof toRequest>) => Promise<unknown>;
  /** After the last one — or Back. */
  onDone: () => void;
}) {
  const [queue] = useState<BookingGarment[]>(() =>
    booking.wearers.flatMap((wearer) => wearer.garments).filter((g) => g.state === 'Returned'),
  );
  const [at, setAt] = useState(0);
  const garment = queue[at];
  const next = queue[at + 1];
  const view = useGarmentInspection(garment?.id ?? null);

  const band = (
    <CounterBand
      eyebrow={`${booking.documentNumber} · ${booking.customerName}${
        garment ? ` · garment ${at + 1} of ${queue.length}` : ''
      }`}
      title="Check each garment"
      onBack={onDone}
      trailing={
        queue.length > 1 && (
          <span
            aria-label={`${at} of ${queue.length} checked`}
            className="block h-1.5 w-56 overflow-hidden rounded-full bg-white/30"
          >
            <span
              className="block h-full rounded-full bg-white"
              style={{ width: `${(at / queue.length) * 100}%` }}
            />
          </span>
        )
      }
    />
  );

  if (!garment) {
    return (
      <>
        {band}
        <Card>
          <p className="mb-3 text-[13px] text-gray-900">
            Nothing on this booking is waiting to be checked.
          </p>
          <Button onClick={onDone}>Back to the booking</Button>
        </Card>
      </>
    );
  }

  return (
    <>
      {band}
      {view.isError ? (
        <Notice tone="danger" title="Its checklist could not be loaded">
          Try again, or check it later from the returns queue.
        </Notice>
      ) : view.isLoading || !view.data ? (
        <Card>
          <p className="text-default text-[13px]">Loading its checklist…</p>
        </Card>
      ) : (
        <InspectionPage
          // A fresh form per garment, never the last one's answers.
          key={garment.id}
          view={view.data}
          saving={saving}
          backLabel="Back to the booking"
          saveLabel={next ? `Save · next: ${next.itemName}` : 'Save and finish'}
          onBack={onDone}
          onSave={(payload) =>
            void onSave(garment.id, payload).then(() => (next ? setAt(at + 1) : onDone()))
          }
        />
      )}
    </>
  );
}
